import { useQuery } from "@apollo/client";
import {
  PlatformSidebar,
  PlatformSidebarItem,
  usePlatformConfig,
} from "@aurem/components";
import { isActiveAssignment } from "hooks/employee";
import { useBasePath } from "hooks/useBasePath";
import { Home, LifeBuoyIcon } from "lucide-react";
import { useChatwoot } from "providers/ChatwootProvider";
import { useI18n } from "providers/I18nProvider";
import { useLocation } from "react-router-dom";
import { gql } from "types";

const GET_USER_COMPANIES = gql(/* GraphQL */ `
  query UserInfo {
    user {
      id
      employments {
        id
        productAssignments {
          id
          endDate
        }
        company {
          id
          name
        }
      }
    }
  }
`);

const EXCLUDED_ROUTES = ["/onboarding", "/disclaimers"] as const;

type Props = {
  open?: boolean;
  onClose?: () => void;
};

export const LayoutOuterSidebar = (props: Props) => {
  const { data } = useQuery(GET_USER_COMPANIES);
  const { pathname } = useLocation();

  const employments = data?.user.employments ?? [];

  const shouldHide = EXCLUDED_ROUTES.some((route) => pathname.includes(route));

  if (employments.length === 0 || shouldHide) return null;

  return <LayoutOuterSidebarView employments={employments} {...props} />;
};

type ViewProps = Props & {
  employments: Array<{
    id: string;
    company: {
      id: string;
      name: string;
    };
    productAssignments: Array<{
      id: string;
      endDate?: string | null | undefined;
    }>;
  }>;
};

const LayoutOuterSidebarView = ({ employments, open, onClose }: ViewProps) => {
  const { t } = useI18n();
  const { path } = useBasePath();
  const config = usePlatformConfig();
  const chatwoot = useChatwoot();

  const logoAlt = config?.name
    ? t("apps.admin.common.hubName", config.name)
    : t("apps.admin.common.hubName", "Aurem");

  function getPath(employment: {
    id: string;
    productAssignments: Array<{
      id: string;
      endDate?: string | null | undefined;
    }>;
  }): string {
    const activeAssignment = employment.productAssignments.find((assignment) =>
      isActiveAssignment(assignment.endDate),
    );
    const assignment = activeAssignment ?? employment.productAssignments[0];
    return path(`employment/${employment.id}/${assignment?.id}/dashboard`);
  }

  const renderEmploymentItem = (
    employment: ViewProps["employments"][number],
  ) => {
    const dashboardPath = getPath(employment);

    return (
      <PlatformSidebarItem
        key={employment.id}
        to={dashboardPath}
        name={employment.company.name}
        label={employment.company.name}
        onClick={onClose}
      />
    );
  };

  return (
    <PlatformSidebar
      logoAlt={logoAlt}
      logoUrl={config.logoUrlSymbol}
      open={open}
      onClose={onClose}
      footer={
        chatwoot.enabled ? (
          <PlatformSidebarItem
            icon={<LifeBuoyIcon size={20} />}
            label={t("apps.employee.navigation.support")}
            onClick={chatwoot.show}
          />
        ) : null
      }
    >
      <PlatformSidebarItem
        to={path("overview")}
        label={t("apps.employee.pages.overView.heading")}
        icon={<Home size={20} />}
        onClick={onClose}
      />
      {employments.map(renderEmploymentItem)}
    </PlatformSidebar>
  );
};
